/**
 * Storage facade for ScoringPresets.
 *
 * The store is a thin wrapper around the axios service functions in
 * services/api.js. UI components import from this module instead of api.js
 * directly so we can:
 *   - normalize backend payloads (unwrap axios `data`)
 *   - centralize JSON export/import (no backend round-trip)
 *   - provide a stable surface if/when we add an offline cache or mirror to
 *     localStorage
 *
 * All async methods return promises that resolve to plain objects (or void
 * for delete/clear operations) and reject with the underlying axios error so
 * callers can read error.response?.data?.columns to surface validation messages.
 */

import {
  getScoringPresets,
  getScoringPreset,
  saveScoringPreset,
  updateScoringPreset,
  deleteScoringPreset,
  setDefaultScoringPreset,
  clearDefaultScoringPreset,
  getTickerGroups,
  saveTickerGroup,
  updateTickerGroup,
  deleteTickerGroup,
} from '../../services/api';

const PRESET_EXPORT_VERSION = 1;
const SCHEMA_VERSION = 1;

/** List all presets owned by the current user. */
export async function listPresets() {
  const { data } = await getScoringPresets();
  // The endpoint may return a bare array OR a paginated object.
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

/** Fetch one preset by ID (full payload). */
export async function fetchPreset(id) {
  const { data } = await getScoringPreset(id);
  return data;
}

/** Create a new preset. `payload` should be a full preset shape minus id/timestamps. */
export async function createPreset(payload) {
  const { data } = await saveScoringPreset(normalizeForSave(payload));
  return data;
}

/** Replace an existing preset (PUT). */
export async function savePreset(id, payload) {
  const { data } = await updateScoringPreset(id, normalizeForSave(payload));
  return data;
}

/** Delete a preset by ID. Resolves to void. */
export async function removePreset(id) {
  await deleteScoringPreset(id);
}

/** Mark a preset as the user's default. */
export async function markDefault(id) {
  const { data } = await setDefaultScoringPreset(id);
  return data; // { default_scoring_preset_id }
}

/** Clear the user's default-preset selection. */
export async function clearDefault(id) {
  const { data } = await clearDefaultScoringPreset(id);
  return data; // { default_scoring_preset_id: null }
}

/**
 * Duplicate an existing preset under a new name. Server-side enforces unique
 * (user, name) so the caller is responsible for picking a unique name.
 */
export async function duplicatePreset(source, newName) {
  return createPreset({
    name: newName,
    description: source.description || '',
    schema_version: source.schema_version ?? SCHEMA_VERSION,
    columns: source.columns,
    aggregate: source.aggregate || {},
  });
}

// ---------------------------------------------------------------------------
// JSON export / import (purely client-side; no backend hit)
// ---------------------------------------------------------------------------

/**
 * Serialize a preset to a JSON string suitable for download / sharing.
 * `id`, `created_at`, and `updated_at` are stripped — the export is an
 * `isDefault`-free, owner-free shape that can be imported by any other user.
 */
export function exportPresetToJson(preset) {
  const exportable = {
    export_version: PRESET_EXPORT_VERSION,
    schema_version: preset.schema_version ?? SCHEMA_VERSION,
    name: preset.name,
    description: preset.description || '',
    columns: preset.columns,
    aggregate: preset.aggregate || {},
  };
  return JSON.stringify(exportable, null, 2);
}

/**
 * Parse a JSON-serialized preset and return a creatable payload.
 * Throws an Error with a user-readable message if the input is malformed.
 */
export function parsePresetFromJson(jsonString) {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch (err) {
    throw new Error('Could not parse JSON: ' + err.message);
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Imported file is not an object');
  }
  if (parsed.export_version !== PRESET_EXPORT_VERSION) {
    throw new Error(
      `Unsupported export_version ${parsed.export_version}. This client expects ${PRESET_EXPORT_VERSION}.`,
    );
  }
  if (typeof parsed.name !== 'string' || !parsed.name.trim()) {
    throw new Error('Imported preset is missing a name');
  }
  if (!Array.isArray(parsed.columns) || parsed.columns.length === 0) {
    throw new Error('Imported preset has no columns');
  }
  return {
    name: parsed.name,
    description: parsed.description || '',
    schema_version: parsed.schema_version ?? SCHEMA_VERSION,
    columns: parsed.columns,
    aggregate: parsed.aggregate || {},
  };
}

// ---------------------------------------------------------------------------
// Ticker groups (favorite groupings of plain ticker symbols)
// ---------------------------------------------------------------------------

export async function listTickerGroups() {
  const { data } = await getTickerGroups();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export async function createTickerGroup(name, tickers) {
  const { data } = await saveTickerGroup({ name: (name || '').trim(), tickers });
  return data;
}

export async function renameOrUpdateTickerGroup(id, name, tickers) {
  const { data } = await updateTickerGroup(id, { name: (name || '').trim(), tickers });
  return data;
}

export async function removeTickerGroup(id) {
  await deleteTickerGroup(id);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function normalizeForSave(payload) {
  return {
    name: (payload.name || '').trim(),
    description: payload.description || '',
    schema_version: payload.schema_version ?? SCHEMA_VERSION,
    columns: payload.columns,
    aggregate: payload.aggregate || {},
  };
}
